<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-10">

        <!-- Page Header -->
        <header class="page-header tw-mb-6">
          <div>
            <h2 class="page-title">Client Claims</h2>
            <p class="page-subtitle">View and manage all client insurance claims.</p>
          </div>
          <div class="stats-chip">
            <span class="stats-chip-label">Total Claims</span>
            <span class="stats-chip-value">{{ totalClaimCount }}</span>
          </div>
        </header>

        <!-- Search Bar -->
        <div class="tw-mb-5">
          <div class="search-wrapper">
            <v-icon class="search-icon" color="#94A3B8">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by client name or claim risk..."
              class="search-input"
            />
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
              <v-icon size="18" color="#94A3B8">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="claims-card claims-loading-card">
          <div class="claims-shimmer-table">
            <div class="claims-shimmer-header">
              <div class="shimmer shimmer-header-client"></div>
              <div class="shimmer shimmer-header-product"></div>
              <div class="shimmer shimmer-header-risk"></div>
              <div class="shimmer shimmer-header-date"></div>
              <div class="shimmer shimmer-header-status"></div>
              <div class="shimmer shimmer-header-actions"></div>
            </div>

            <div
              v-for="n in 6"
              :key="`claims-shimmer-${n}`"
              class="claims-shimmer-row"
            >
              <div class="shimmer shimmer-client"></div>

              <div class="shimmer shimmer-product"></div>

              <div class="shimmer shimmer-risk"></div>

              <div class="shimmer shimmer-date"></div>

              <div class="shimmer shimmer-status"></div>

              <div class="shimmer shimmer-action"></div>
            </div>
          </div>
        </div>

        <!-- Table Card -->
        <div v-else class="claims-card" :class="{ 'card-loading': pageLoading }">
          <div v-if="pageLoading" class="table-overlay">
            <v-progress-circular :size="40" color="#00AEEF" indeterminate />
          </div>
          <div v-if="displayClaims.length > 0" class="table-scroll-wrap">
            <table class="claims-table">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Product</th>
                <th>Claim Risk</th>
                <th class="th-sortable" @click="toggleDateSort">
                  Claim Date
                  <v-icon v-if="dateSortOrder === 'desc'" size="14" class="sort-icon">mdi-arrow-down</v-icon>
                  <v-icon v-else size="14" class="sort-icon">mdi-arrow-up</v-icon>
                </th>
                <th>Claim Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="claim in displayClaims"
                :key="claim.claimNo"
                class="claims-row"
                @click="viewClaimDetails(claim)"
              >
                <td>
                  <div class="client-name">{{ claim.clientName }}</div>
                </td>
                <td class="cell-text">{{ claim.productDesc }}</td>
                <td class="cell-text">{{ claim.propertyId || '—' }}</td>
                <td class="cell-text cell-muted">{{ claim.claimDate }}</td>
                <td>
                  <span :class="['cover-badge', claim.claimStatus === 'Closed Settled' ? 'badge-comprehensive' : claim.claimStatus && claim.claimStatus.toLowerCase().includes('reject') ? 'badge-tpo' : 'badge-other']">
                    {{ claim.claimStatus || '—' }}
                  </span>
                </td>
                <td>
                  <button class="btn-view" @click.stop="viewClaimDetails(claim)">
                    View Claim Details
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          </div>

          <!-- Pagination -->
          <div v-if="displayClaims.length > 0 || totalFilteredRecords > 0" class="pagination-bar">
            <span class="pagination-info">
              Showing {{ rangeStart }}-{{ rangeEnd }} of {{ totalClaimCount }}
            </span>
            <div class="pagination-controls">
              <button
                class="pagination-btn"
                :disabled="currentPage <= 1 || pageLoading"
                @click="goToPage(currentPage - 1)"
              >
                Previous
              </button>
              <span class="pagination-page">Page {{ currentPage }} of {{ totalPages }}</span>
              <button
                class="pagination-btn"
                :disabled="currentPage >= totalPages || pageLoading"
                @click="goToPage(currentPage + 1)"
              >
                Next
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <v-icon size="60" color="#CBD5E1">mdi-file-document-outline</v-icon>
            <p class="empty-title">No claims found</p>
            <p class="empty-subtitle">Try adjusting your search query.</p>
          </div>
        </div>

      </div>
    </div>
  </agent-default>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import axios from 'axios';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'AgentClientClaims',
  components: { AgentDefault },
  data() {
    return {
      loading: true,
      pageLoading: false,
      searchQuery: '',
      searchDebounced: '',
      claims: [],
      totalFilteredRecords: 0,
      currentPage: 1,
      rowsPerPage: 20,
      searchDebounceTimer: null,
      dateSortOrder: 'desc',
      searchFetchLimit: 500, // fetch more when searching to allow full-data search
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser', 'isAuthenticated']),
    totalClaimCount() {
      if (this.searchDebounced) return this.filteredAndSortedClaims.length;
      return this.totalFilteredRecords || this.claims.length;
    },
    filteredAndSortedClaims() {
      let list = this.claims;
      if (this.searchDebounced) {
        const q = this.searchDebounced.toLowerCase();
        const matches = (c) => {
          const fields = [c.clientName, c.truncClientName, c.insuredName, c.truncInsuredName, c.propertyId].filter(Boolean);
          return fields.some((f) => String(f).toLowerCase().includes(q));
        };
        list = list.filter(matches);
      }
      const getSortTime = (c) => {
        const raw = c.claimDate || c.lossDateTime || '';
        if (!raw) return 0;
        const t = new Date(raw).getTime();
        return Number.isNaN(t) ? 0 : t;
      };
      const asc = this.dateSortOrder === 'asc';
      return [...list].sort((a, b) => (asc ? getSortTime(a) - getSortTime(b) : getSortTime(b) - getSortTime(a)));
    },
    displayClaims() {
      const all = this.filteredAndSortedClaims;
      if (this.searchDebounced) {
        const start = (this.currentPage - 1) * this.rowsPerPage;
        return all.slice(start, start + this.rowsPerPage);
      }
      return all;
    },
    totalPages() {
      const total = this.searchDebounced ? this.filteredAndSortedClaims.length : (this.totalFilteredRecords || 0);
      return total <= 0 ? 1 : Math.ceil(total / this.rowsPerPage);
    },
    rangeStart() {
      const total = this.searchDebounced ? this.filteredAndSortedClaims.length : (this.totalFilteredRecords || 0);
      if (total <= 0) return 0;
      return (this.currentPage - 1) * this.rowsPerPage + 1;
    },
    rangeEnd() {
      const total = this.searchDebounced ? this.filteredAndSortedClaims.length : (this.totalFilteredRecords || 0);
      const end = this.currentPage * this.rowsPerPage;
      return total <= 0 ? 0 : Math.min(end, total);
    },
  },
  watch: {
    searchQuery(val) {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        const next = (val || '').trim();
        const wasSearching = Boolean(this.searchDebounced);
        this.searchDebounced = next;
        this.currentPage = 1;
        if (next || wasSearching) this.fetchClaims();
      }, 400);
    },
  },
  mounted() {
    this.fetchClaims();
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    async getAgnCode() {
      const email = this.authenticatedUser?.email;
      const cacheKey = email ? `agentCodeClaims_${email}` : 'agentCodeClaims';
      const cached = localStorage.getItem(cacheKey);
      if (cached) return cached;
      const agnCode = this.authenticatedUser?.['custom:agent_code'];
      if (!agnCode && !email) return null;
      try {
        const baseUrl = '/customer/gis/agent/dashboard/summary';
        const url = agnCode
          ? `${baseUrl}?agnCode=${agnCode}`
          : `${baseUrl}?email=${email}`;
        const res = await axios.get(url, { validateStatus: () => true });
        if (res.status === 200 && res.data) {
          const code = res.data.agent?.code;
          if (code !== null && code !== undefined) {
            const val = String(code);
            localStorage.setItem(cacheKey, val);
            return val;
          }
        }
      } catch {
        // ignore
      }
      return null;
    },
    async fetchClaims() {
      const isInitial = !this.claims.length && this.totalFilteredRecords === 0;
      if (isInitial) this.loading = true;
      else this.pageLoading = true;
      try {
        const agnCode = await this.getAgnCode();
        if (!agnCode) {
          this.claims = [];
          this.totalFilteredRecords = 0;
          return;
        }
        const isSearchMode = Boolean(this.searchDebounced);
        const limit = isSearchMode ? this.searchFetchLimit : this.rowsPerPage;
        const pageNumber = isSearchMode ? 1 : this.currentPage;
        const params = new URLSearchParams({
          agnCode,
          limit: String(limit),
          pageNumber: String(pageNumber),
          sortBy: 'claimDate',
          sortOrder: this.dateSortOrder,
        });
        const url = `/customer/gis/agent/claims?${params.toString()}`;
        const res = await axios.get(url, { validateStatus: () => true });
        if (res.status !== 200 || !res.data) {
          this.claims = [];
          this.totalFilteredRecords = 0;
          return;
        }
        const obj = res.data?.object;
        const list = obj?.pageItems ? obj.pageItems : [];
        this.claims = list;
        this.totalFilteredRecords = obj?.totalFilteredRecords ?? list.length;
      } catch (err) {
        if (this.$toast && this.$toast.error) {
          this.$toast.error(err?.message || 'Failed to load claims', 'Error');
        }
        this.claims = [];
        this.totalFilteredRecords = 0;
      } finally {
        this.loading = false;
        this.pageLoading = false;
      }
    },
    toggleDateSort() {
      this.dateSortOrder = this.dateSortOrder === 'desc' ? 'asc' : 'desc';
      trackAgentInteraction(this, 'claims_toggle_sort', { order: this.dateSortOrder });
      this.currentPage = 1;
      if (!this.searchDebounced) this.fetchClaims();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      trackAgentInteraction(this, 'claims_change_page', { page });
      if (!this.searchDebounced) this.fetchClaims();
    },
    viewClaimDetails(claim) {
      trackAgentInteraction(this, 'claims_view_details', {
        claim_no: claim?.claimNo || 'unknown',
      });
      const key = `agentClaim_${claim.claimNo}`;
      try {
        sessionStorage.setItem(key, JSON.stringify(claim));
      } catch {
        // ignore if storage fails
      }
      this.$router.push({ name: 'AgentClientClaimDetails', params: { claimId: claim.claimNo }, query: { insuredName: claim.clientName } });
    },
  },
  metaInfo: { title: 'View Client Claims' },
};
</script>

<style scoped>
/* ── Page Header ── */
.page-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

/* ── Stats Chip ── */
.stats-chip {
  background: linear-gradient(135deg, #1E4276 0%, #2A5A9E 100%);
  border-radius: 14px;
  padding: 14px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 110px;
  box-shadow: 0 4px 12px rgba(30, 66, 118, 0.2);
}

.stats-chip-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stats-chip-value {
  font-size: 2rem;
  font-weight: 800;
  color: white;
  line-height: 1.1;
  margin-top: 2px;
}

/* ── Search ── */
.search-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  padding: 0 14px;
  max-width: 380px;
  height: 48px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-wrapper:focus-within {
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
}

.search-icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.9rem;
  color: #1E4276;
  background: transparent;
}

.search-input::placeholder {
  color: #94A3B8;
}

.search-clear {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
}

/* ── Claims Card ── */
.claims-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  position: relative;
}

.claims-card.card-loading {
  min-height: 200px;
}

.table-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

/* ── Table ── */
.table-scroll-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 1px;
}

.claims-table {
  width: 100%;
  min-width: 700px;
  border-collapse: collapse;
}

@media (max-width: 640px) {
  .claims-table th,
  .claims-table td {
    padding: 12px 14px;
    font-size: 0.85rem;
  }
}

.claims-table thead tr {
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.th-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.th-sortable:hover {
  color: #00AEEF;
}

.th-sortable .sort-icon {
  margin-left: 4px;
  vertical-align: middle;
  opacity: 0.8;
}

.claims-table th {
  text-align: left;
  padding: 14px 20px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.claims-row {
  border-bottom: 1px solid #F1F5F9;
  cursor: pointer;
  transition: background 0.15s;
}

.claims-row:last-child {
  border-bottom: none;
}

.claims-row:hover {
  background: #F0F9FF;
}

.claims-table td {
  padding: 16px 20px;
  vertical-align: middle;
}

.client-name {
  font-weight: 700;
  color: #1E4276;
  font-size: 0.95rem;
}

.cell-text {
  font-size: 0.9rem;
  color: #334155;
}

.cell-muted {
  color: #94A3B8;
  font-size: 0.875rem;
}

/* ── Cover Badges ── */
.cover-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.badge-comprehensive {
  background: #EBF8FF;
  color: #00AEEF;
}

.badge-tpo {
  background: #FFF5F5;
  color: #E53E3E;
}

.badge-other {
  background: #FEFCBF;
  color: #B7791F;
}

/* ── View Button ── */
.btn-view {
  background: transparent;
  border: 1.5px solid #00AEEF;
  color: #00AEEF;
  padding: 6px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
}

.btn-view:hover {
  background: #00AEEF;
  color: white;
}

/* ── Pagination ── */
.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #E8EDF2;
  background: #F8FAFC;
}

.pagination-info {
  font-size: 0.875rem;
  color: #64748B;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pagination-btn {
  padding: 8px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1E4276;
  background: white;
  border: 1.5px solid #E2E8F0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.pagination-btn:hover:not(:disabled) {
  border-color: #00AEEF;
  color: #00AEEF;
  background: #EBF8FF;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-page {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1E4276;
}

/* ── Empty State ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 16px 0 6px;
}

.empty-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}
/* ── Claims Table Shimmer ── */

.claims-loading-card {
  overflow: hidden;
}

.claims-shimmer-table {
  width: 100%;
  overflow-x: auto;
}

.claims-shimmer-header,
.claims-shimmer-row {
  display: grid;
  grid-template-columns:
    minmax(150px, 1.3fr)
    minmax(130px, 1fr)
    minmax(110px, 0.9fr)
    minmax(120px, 1fr)
    minmax(120px, 1fr)
    minmax(150px, 1.1fr);
  align-items: center;
  column-gap: 20px;
  min-width: 700px;
  padding-left: 20px;
  padding-right: 20px;
}

.claims-shimmer-header {
  min-height: 48px;
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.claims-shimmer-row {
  min-height: 72px;
  border-bottom: 1px solid #F1F5F9;
}

.shimmer {
  position: relative;
  overflow: hidden;
  background: #E9EEF3;
  border-radius: 6px;
}

.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.65) 50%,
    transparent 100%
  );
  animation: claims-shimmer 1.5s infinite;
}

@keyframes claims-shimmer {
  0% {
    left: -150%;
  }

  100% {
    left: 150%;
  }
}

/* Header placeholders */

.shimmer-header-client {
  width: 90px;
  height: 12px;
}

.shimmer-header-product {
  width: 65px;
  height: 12px;
}

.shimmer-header-risk {
  width: 75px;
  height: 12px;
}

.shimmer-header-date {
  width: 75px;
  height: 12px;
}

.shimmer-header-status {
  width: 90px;
  height: 12px;
}

.shimmer-header-actions {
  width: 70px;
  height: 12px;
}

/* Row placeholders */

.shimmer-client {
  width: 130px;
  height: 16px;
}

.shimmer-product {
  width: 105px;
  height: 15px;
}

.shimmer-risk {
  width: 75px;
  height: 15px;
}

.shimmer-date {
  width: 90px;
  height: 15px;
}

.shimmer-status {
  width: 95px;
  height: 26px;
  border-radius: 20px;
}

.shimmer-action {
  width: 125px;
  height: 32px;
  border-radius: 8px;
}
</style>
